import React, { useEffect, useRef, useState } from 'react';
import { updateParticipant, uploadGuestPhoto, removeGuestPhoto } from '../firebase/firestoreService';
import { updateCCBProfile, uploadCCBProfileImageV2, deleteCCBProfileImageV2 } from '../firebase/functionsService';
import { resizeImage } from '../utils/imageUtils';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import ConfirmDialog from './ConfirmDialog';
import { useAppBusy } from '../utils/appUpdate';
import { readOnlyProps } from '../utils/viewAs';
import '../styles/AddNoteModal.css';

// Map app field names to CCB API field names. `original` is the pre-edit
// guest data, used only for the Mobile Phone override below.
function mapToCCBFields(formData, original = {}) {
  const ccbData = {};
  if (formData.firstName) ccbData.first_name = formData.firstName;
  if (formData.lastName) ccbData.last_name = formData.lastName;
  if (formData.gender) ccbData.gender = formData.gender;
  if (formData.birthday) ccbData.birthday = formData.birthday;
  if (formData.email) ccbData.email = formData.email;

  // Phone routing — CCB's contact_phone is effectively read-only via the
  // V1 API: it auto-derives from mobile_phone, and direct contact_phone
  // writes are silently ignored when mobile_phone is in the request.
  //
  // Contact Phone: always sent (when non-empty) to BOTH contact_phone and
  // mobile_phone, so the value actually persists.
  //
  // Mobile Phone: overrides mobile_phone ONLY if the user actually edited
  // this field. If we always overrode based on the form's current Mobile
  // value, a stale unchanged Mobile would clobber the Contact Phone
  // routing every time. Comparing to the original guest data tells us
  // whether the user really intended a different mobile.
  if (formData.phone) {
    ccbData.contact_phone = formData.phone;
    ccbData.mobile_phone = formData.phone;
  }
  const mobileChanged = formData.mobilePhone !== (original.mobilePhone || '');
  if (mobileChanged && formData.mobilePhone) {
    ccbData.mobile_phone = formData.mobilePhone;
  }
  if (formData.homePhone) ccbData.home_phone = formData.homePhone;
  if (!ccbData.contact_phone && (ccbData.mobile_phone || formData.homePhone)) {
    ccbData.contact_phone = ccbData.mobile_phone || formData.homePhone;
  }

  if (formData.street) ccbData.mailing_street_address = formData.street;
  if (formData.city) ccbData.mailing_city = formData.city;
  if (formData.state) ccbData.mailing_state = formData.state;
  if (formData.zip) ccbData.mailing_zip = formData.zip;
  // Emergency contact fields
  if (formData.emergencyContactName) ccbData.emergency_contact_name = formData.emergencyContactName;
  if (formData.emergencyContactPhone) ccbData.emergency_phone = formData.emergencyContactPhone;
  return ccbData;
}

// Check if guest ID indicates a CCB profile
function isCCBGuest(guestId) {
  return guestId && guestId.startsWith('ccb_');
}

function EditGuestModal({ guest, onClose, onSave }) {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [formData, setFormData] = useState({
    firstName: guest.firstName || '',
    lastName: guest.lastName || '',
    gender: guest.gender || '',
    birthday: guest.birthday || '',
    email: guest.email || '',
    phone: guest.phone || '',
    mobilePhone: guest.mobilePhone || '',
    homePhone: guest.homePhone || '',
    street: guest.street || '',
    city: guest.city || '',
    state: guest.state || '',
    zip: guest.zip || '',
    emergencyContactName: guest.emergencyContactName || '',
    emergencyContactPhone: guest.emergencyContactPhone || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  // The modal is only mounted while open, and anything in it (field edits,
  // staged photo changes) is lost on a reload — hold off the auto-reload.
  useAppBusy('edit-profile', true);
  const [ccbSyncStatus, setCcbSyncStatus] = useState(null);
  // Photo changes are staged locally and only applied when the user clicks
  // Save Changes. Picking a file or clicking Remove must never write to
  // Storage, Firestore, or CCB on its own — closing/cancelling the modal
  // discards the staged change and leaves the existing photo untouched.
  // pendingPhoto: null | { action: 'replace', blob, previewUrl } | { action: 'remove' }
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const fileInputRef = useRef(null);

  const isFromCCB = isCCBGuest(guest.id);
  const savedImage = guest.image || null;
  const displayImage = pendingPhoto?.action === 'replace'
    ? pendingPhoto.previewUrl
    : pendingPhoto?.action === 'remove' ? null : savedImage;

  // Release the object URL for a staged preview when it's replaced or the
  // modal unmounts.
  const previewUrl = pendingPhoto?.previewUrl;
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const isDirty = pendingPhoto !== null || Object.keys(formData).some(
    (key) => formData[key] !== (guest[key] || '')
  );

  // Every close path (X, Cancel, overlay click) goes through here so unsaved
  // edits — especially a staged photo — are never silently dropped or saved.
  const requestClose = () => {
    if (submitting) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const handlePhotoButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleRemovePhotoConfirmed = () => {
    setShowRemoveConfirm(false);
    setPendingPhoto(savedImage ? { action: 'remove' } : null);
  };

  const handlePhotoFileSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setPhotoProcessing(true);
    setMessage(null);
    try {
      const resized = await resizeImage(file, 800, 800, 0.85);
      setPendingPhoto({
        action: 'replace',
        blob: resized,
        previewUrl: URL.createObjectURL(resized)
      });
    } catch (err) {
      console.error('Photo processing error:', err);
      setMessage({ type: 'error', text: `Could not read photo: ${err.message || 'Unknown error'}` });
    } finally {
      setPhotoProcessing(false);
    }
  };

  // Apply the staged photo change. Called only from handleSubmit. Returns a
  // warning string if the app copy saved but the CCB push failed.
  const applyPendingPhoto = async () => {
    if (!pendingPhoto) return null;
    const numericId = isFromCCB ? guest.id.replace('ccb_', '') : null;

    if (pendingPhoto.action === 'remove') {
      await removeGuestPhoto(guest.id);
      if (!isFromCCB) return null;
      try {
        await deleteCCBProfileImageV2({ individualId: numericId });
        return null;
      } catch (ccbErr) {
        console.error('CCB image delete failed:', ccbErr);
        return `Photo removed from app, but CCB delete failed: ${ccbErr.message || 'Unknown error'}. You may need to remove it in CCB directly.`;
      }
    }

    const photoURL = await uploadGuestPhoto(guest.id, pendingPhoto.blob);
    // uploadGuestPhoto's Cloud Function already wrote image + imageSource:'app'
    if (!isFromCCB) return null;
    try {
      await uploadCCBProfileImageV2({ individualId: numericId, imageUrl: photoURL });
      await updateParticipant(guest.id, { ccbImageUploaded: true });
      return null;
    } catch (ccbErr) {
      console.error('CCB image push failed:', ccbErr);
      await updateParticipant(guest.id, { ccbImageUploaded: false });
      return 'Photo saved locally; CCB push failed (will retry on next sync).';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setMessage({
        type: 'error',
        text: 'First name and last name are required.'
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    setCcbSyncStatus(null);

    try {
      // Compute CCB payload first so we can mirror any phone-routing
      // decisions back into the local Firestore write — keeps the app's
      // copy in sync with what we actually push to CCB (otherwise the
      // user could see Mobile Phone show one value locally while CCB has
      // another until the next sync run).
      const ccbData = isFromCCB ? mapToCCBFields(formData, guest) : null;
      const adjustedFormData = { ...formData };
      if (ccbData?.mobile_phone && ccbData.mobile_phone !== formData.mobilePhone) {
        adjustedFormData.mobilePhone = ccbData.mobile_phone;
      }

      await updateParticipant(guest.id, adjustedFormData);

      // Track the outcome locally — React state set above isn't readable
      // until the next render, so it can't drive the auto-close decision.
      let result = {
        type: 'success',
        text: isFromCCB ? 'Profile updated locally and synced to CCB!' : 'Profile updated successfully!'
      };

      if (isFromCCB) {
        setCcbSyncStatus('syncing');
        try {
          await updateCCBProfile({
            individualId: guest.id,
            profileData: ccbData
          });
          // Clear any pending-sync flag from a previous failed attempt so
          // the reconciler stops trying to push stale state.
          await updateParticipant(guest.id, {
            needsCCBProfileUpdate: false,
            lastCCBUpdateError: null,
            ccbUpdateAttemptCount: 0
          });
          setCcbSyncStatus('success');
        } catch (ccbError) {
          console.error('Error syncing to CCB:', ccbError);
          // Flag the doc so scheduledCCBProfileUpdateReconcile retries the
          // sync against the current Firestore state every 30 minutes.
          try {
            await updateParticipant(guest.id, {
              needsCCBProfileUpdate: true,
              lastCCBUpdateError: ccbError.message || String(ccbError)
            });
          } catch (flagError) {
            console.error('Could not flag participant for reconcile:', flagError);
          }
          setCcbSyncStatus('error');
          result = {
            type: 'warning',
            text: `Profile saved locally, CCB sync failed (will auto-retry): ${ccbError.message}`
          };
        }
      }

      // Staged photo change is applied only here, on an explicit Save.
      if (pendingPhoto) {
        try {
          const photoWarning = await applyPendingPhoto();
          setPendingPhoto(null);
          if (photoWarning) result = { type: 'warning', text: photoWarning };
        } catch (photoErr) {
          console.error('Photo save error:', photoErr);
          result = {
            type: 'error',
            text: `Profile saved, but the photo change failed: ${photoErr.message || 'Unknown error'}`
          };
        }
      }

      setMessage(result);

      // Auto-close on success (after the toast has been visible long
      // enough to read). On error/warning, leave the modal open so the
      // user can see the message and decide what to do.
      if (result.type === 'success') {
        setTimeout(() => {
          if (onSave) onSave();
          onClose();
        }, 2200);
      } else if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({
        type: 'error',
        text: `Failed to update profile: ${error.message}`
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        // Ignore clicks that bubble up from the nested ConfirmDialogs.
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div className="modal-content add-note-modal edit-guest-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Profile - {guest.firstName} {guest.lastName}</h2>
          {isFromCCB && (
            <span className="ccb-badge" title="This profile is linked to CCB">CCB</span>
          )}
          <button type="button" className="modal-close" onClick={requestClose} aria-label="Close">X</button>
        </div>

        {message && (
          <div className={`edit-toast edit-toast-${message.type}`} role="status" aria-live="polite">
            <span className="edit-toast-icon" aria-hidden="true">
              {message.type === 'success' ? '✓' : message.type === 'error' ? '✕' : '⚠'}
            </span>
            <span className="edit-toast-text">{message.text}</span>
            <button
              type="button"
              className="edit-toast-close"
              onClick={() => setMessage(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {isFromCCB && (
          <div className="ccb-sync-notice">
            Changes will be synced to Church Community Builder (CCB).
            {ccbSyncStatus === 'syncing' && <span className="sync-status syncing"> Syncing...</span>}
            {ccbSyncStatus === 'success' && <span className="sync-status success"> Synced!</span>}
            {ccbSyncStatus === 'error' && <span className="sync-status error"> Sync failed</span>}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handlePhotoFileSelected}
        />

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Profile Photo */}
            <div className="edit-section edit-photo-section">
              <h4>Profile Photo</h4>
              <div className="edit-photo-row">
                <div className="edit-photo-preview">
                  <img
                    src={displayImage || placeholderAvatar}
                    alt={`${guest.firstName} ${guest.lastName}`}
                    onError={(e) => { e.currentTarget.src = placeholderAvatar; }}
                  />
                </div>
                <div className="edit-photo-actions">
                  <div className="edit-photo-buttons">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePhotoButtonClick}
                      disabled={photoProcessing || submitting || ro.disabled}
                      title={ro.title}
                    >
                      {photoProcessing
                        ? 'Working…'
                        : displayImage ? 'Replace Photo' : 'Add Photo'}
                    </button>
                    {displayImage && (
                      <button
                        type="button"
                        className="btn btn-danger-outline"
                        onClick={() => setShowRemoveConfirm(true)}
                        disabled={photoProcessing || submitting || ro.disabled}
                        title={ro.title}
                      >
                        Remove Photo
                      </button>
                    )}
                    {pendingPhoto && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setPendingPhoto(null)}
                        disabled={photoProcessing || submitting}
                      >
                        Undo Photo Change
                      </button>
                    )}
                  </div>
                  {pendingPhoto ? (
                    <p className="edit-photo-hint">
                      {pendingPhoto.action === 'remove' ? 'Photo will be removed' : 'New photo will be saved'} when you click Save Changes.
                    </p>
                  ) : isFromCCB && (
                    <p className="edit-photo-hint">
                      Photo changes are saved, and synced to CCB, when you click Save Changes.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="edit-section">
              <h4>Personal Information</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={submitting}
                  >
                    <option value="">Select...</option>
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Birthday</label>
                  <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="edit-section">
              <h4>Contact Information</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Mobile Phone</label>
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Home Phone</label>
                  <input
                    type="tel"
                    name="homePhone"
                    value={formData.homePhone}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="edit-section">
              <h4>Address Information</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>ZIP</label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="edit-section">
              <h4>Emergency Contact</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Emergency Contact Name</label>
                  <input
                    type="text"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Emergency Contact Phone</label>
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={requestClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || ro.disabled}
              title={ro.title}
            >
              {submitting ? (isFromCCB ? 'Saving & Syncing...' : 'Saving...') : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={showRemoveConfirm}
        title="Remove profile photo?"
        message={
          isFromCCB
            ? 'The photo will be deleted from the app and from CCB when you click Save Changes.'
            : 'The photo will be deleted from the app when you click Save Changes.'
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleRemovePhotoConfirmed}
        onCancel={() => setShowRemoveConfirm(false)}
      />

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Discard unsaved changes?"
        message={
          pendingPhoto
            ? 'Your changes, including the photo change, have not been saved. The current photo will be kept.'
            : 'Your changes have not been saved.'
        }
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  );
}

export default EditGuestModal;
